import type { ZodIssue } from 'zod';
import {
  JD_FACT_LABELS_DE,
  JD_FACT_LABELS_EN,
  JD_TEAM_PHRASES,
  ProjectType,
  WorkModel,
  jdContentSchema,
  summarizeChecks,
  todayIso,
  type CheckResult,
  type CheckSummary,
  type DocLang,
  type JdContent,
  type JdContentInput,
} from '@clawhire/shared';
import { formatDate } from '@/lib/format';
import { normalizeDescription } from '../description';
import type { PositionDetail } from '../types';

/** What the JD form edits; converted to `JdContent` with `toContent()` before validation. */
export interface JdFormValues {
  lang: 'en' | 'de';
  ref: string;
  date: string;
  status: string;
  title: string;
  subtitle: string;
  intro: string;
  facts: Array<{ label: string; value: string }>;
  /** Paragraphs separated by blank lines. */
  about: string;
  responsibilities: string[];
  mustHave: string[];
  niceToHave: string[];
  process: string[];
  stack: Array<{ name: string; value: string }>;
  /** One line, or several lines for a list. */
  team: string;
  applyText: string;
  applyEmail: string;
  contact?: JdContentInput['contact'];
}

export function factLabels(lang: DocLang): readonly string[] {
  return lang === 'DE' ? JD_FACT_LABELS_DE : JD_FACT_LABELS_EN;
}

/** API prefill (or a previous version's content) → form values. Missing facts get the fixed labels. */
export function fromContent(
  content: Partial<JdContentInput> | null | undefined,
  lang: DocLang,
): JdFormValues {
  const labels = factLabels(lang);
  const facts = labels.map((label, i) => ({
    label: content?.facts?.[i]?.label ?? label,
    value: content?.facts?.[i]?.value ?? '',
  }));
  const stack = content?.stack;
  return {
    lang: lang === 'DE' ? 'de' : 'en',
    ref: content?.ref ?? '',
    date: content?.date ?? todayIso(),
    status: content?.status ?? '',
    title: content?.title ?? '',
    subtitle: content?.subtitle ?? '',
    intro: content?.intro ?? '',
    facts,
    about: Array.isArray(content?.about) ? content.about.join('\n\n') : (content?.about ?? ''),
    responsibilities: [...(content?.responsibilities ?? [])],
    mustHave: [...(content?.mustHave ?? [])],
    niceToHave: [...(content?.niceToHave ?? [])],
    process: [...(content?.process ?? [])],
    stack:
      typeof stack === 'string'
        ? stack.trim()
          ? [{ name: 'Stack', value: stack }]
          : []
        : (stack ?? []).map(([name, value]) => ({ name, value })),
    team: Array.isArray(content?.team) ? content.team.join('\n') : (content?.team ?? ''),
    applyText: content?.applyText ?? '',
    applyEmail: content?.applyEmail ?? '',
    contact: content?.contact,
  };
}

const cleanList = (list: readonly string[]) => list.map((s) => s.trim()).filter(Boolean);
const splitParagraphs = (s: string) =>
  s
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
const splitLines = (s: string) =>
  s
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
const optional = (s: string | undefined) => (s && s.trim() ? s.trim() : undefined);

/** Form values → plain JdContent object (unvalidated). */
export function toContent(values: JdFormValues): Record<string, unknown> {
  const about = splitParagraphs(values.about);
  const teamLines = splitLines(values.team);
  const niceToHave = cleanList(values.niceToHave);
  const stack = values.stack
    .map((r) => [r.name.trim(), r.value.trim()] as [string, string])
    .filter(([a, b]) => a || b);
  const content: Record<string, unknown> = {
    lang: values.lang,
    ref: values.ref.trim(),
    date: values.date,
    status: optional(values.status),
    title: values.title.trim(),
    subtitle: optional(values.subtitle),
    intro: optional(values.intro),
    facts: values.facts.map((f) => ({ label: f.label.trim(), value: f.value.trim() })),
    about: about.length ? about : undefined,
    responsibilities: cleanList(values.responsibilities),
    mustHave: cleanList(values.mustHave),
    niceToHave: niceToHave.length ? niceToHave : undefined,
    stack: stack.length ? stack : undefined,
    team: teamLines.length <= 1 ? (teamLines[0] ?? '') : teamLines,
    process: cleanList(values.process),
    applyText: optional(values.applyText),
    applyEmail: optional(values.applyEmail),
    contact: values.contact,
  };
  return Object.fromEntries(Object.entries(content).filter(([, v]) => v !== undefined));
}

export interface ContentIssue {
  path: string;
  message: string;
}

export function issuePath(issue: ZodIssue): string {
  return issue.path.map(String).join('.');
}

/** Validate with the shared schema; returns the parsed content or the issues (never both). */
export function validateContent(
  values: JdFormValues,
): { content: JdContent; issues: [] } | { content: null; issues: ContentIssue[] } {
  const parsed = jdContentSchema.safeParse(toContent(values));
  if (parsed.success) return { content: parsed.data, issues: [] };
  return {
    content: null,
    issues: parsed.error.issues.map((i) => ({ path: issuePath(i), message: i.message })),
  };
}

/** The API returns either a list of results or a summary; normalise to a summary. */
export function normalizeChecks(checks: unknown): CheckSummary {
  if (Array.isArray(checks)) return summarizeChecks(checks as CheckResult[]);
  if (checks && typeof checks === 'object') {
    const c = checks as Partial<CheckSummary>;
    if (Array.isArray(c.results)) return summarizeChecks(c.results);
    if (Array.isArray(c.errors) || Array.isArray(c.warnings)) {
      return summarizeChecks([...(c.errors ?? []), ...(c.warnings ?? [])]);
    }
  }
  return summarizeChecks([]);
}

/** Pull check results out of a 409 `CHECKS_FAILED` error body. */
export function checksFromErrorDetails(details: unknown): CheckSummary {
  if (details && typeof details === 'object' && 'checks' in details) {
    return normalizeChecks((details as { checks: unknown }).checks);
  }
  return normalizeChecks(details);
}

/**
 * Draft built from the position itself, used when `GET /positions/:id/jd/prefill` is not
 * available. The Rate fact comes from the §5 calculation like on the server.
 */
export function localPrefill(position: PositionDetail, lang: DocLang): JdContentInput {
  const de = lang === 'DE';
  const d = normalizeDescription(position.description);
  const labels = factLabels(lang);
  const onRequest = de ? 'auf Anfrage' : 'on request';
  const location = [position.location, position.workModel ? WorkModel.label(position.workModel) : null]
    .filter(Boolean)
    .join(', ');
  const values = [
    position.department ?? position.intermediary?.name ?? onRequest,
    location || onRequest,
    position.startDate ? formatDate(position.startDate) : de ? 'ab sofort' : 'asap',
    position.endDate ? `${de ? 'bis' : 'until'} ${formatDate(position.endDate)}` : de ? 'offen' : 'open',
    position.workload ?? '100 %',
    de ? position.rates.jdRateTextDe : position.rates.jdRateText,
    position.languages.length ? position.languages.join(', ') : de ? 'Englisch' : 'English',
    position.projectType ? ProjectType.label(position.projectType) : de ? 'Freelance' : 'Freelance',
  ];
  const year = todayIso().slice(0, 4);
  return {
    lang: de ? 'de' : 'en',
    ref: position.jdReference ?? `DC-J-${year}-0000`,
    date: todayIso(),
    status: de ? 'Offen' : 'Open',
    title: position.role,
    intro: d.context || undefined,
    facts: labels.map((label, i) => ({ label, value: values[i] ?? onRequest })),
    responsibilities: d.responsibilities,
    mustHave: d.mustHave,
    niceToHave: d.niceToHave.length ? d.niceToHave : undefined,
    team: JD_TEAM_PHRASES[de ? 'de' : 'en'][0],
    process: de
      ? ['CV-Screening durch data-claw.de', 'Interview mit dem Kunden', 'Vertrag']
      : ['CV screening by data-claw.de', 'Interview with the client', 'Contract'],
  };
}
